import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from 'components/Icons'
import styles from './InstallPrompt.module.scss'

const STORAGE_KEY = 'pwa-install-dismissed'

/**
 * Banner offering to install the app as a PWA.
 * Listens to beforeinstallprompt, shows orange banner at bottom.
 * Dismissed state is persisted to localStorage.
 */
const InstallPrompt = () => {
  const { t } = useTranslation('common')
  const [deferred, setDeferred] = useState(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    let dismissed = false
    try {
      dismissed = localStorage.getItem(STORAGE_KEY) === '1'
    } catch (_) {
      /* ignore */
    }
    if (dismissed) return undefined

    const onBeforeInstall = (e) => {
      e.preventDefault()
      setDeferred(e)
      setVisible(true)
    }
    const onInstalled = () => {
      setVisible(false)
      setDeferred(null)
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstall)
    window.addEventListener('appinstalled', onInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [])

  const handleDismiss = () => {
    setVisible(false)
    try {
      localStorage.setItem(STORAGE_KEY, '1')
    } catch (_) {
      /* ignore */
    }
  }

  const handleInstall = async () => {
    if (!deferred) return
    deferred.prompt()
    try {
      await deferred.userChoice
    } catch (_) {
      /* ignore */
    }
    setDeferred(null)
    setVisible(false)
  }

  if (!visible) return null

  return (
    <div className={styles.banner} role="dialog" aria-labelledby="install-prompt-title">
      <span className={styles.icon} aria-hidden="true">
        <Icon name="download" size={18} color="#fff" />
      </span>
      <div className={styles.body}>
        <div id="install-prompt-title" className={styles.title}>
          {t('auto.installPrompt.title', 'Установить приложение')}
        </div>
        <div className={styles.subtitle}>
          {t('auto.installPrompt.subtitle', 'Быстрый доступ с главного экрана')}
        </div>
      </div>
      <button
        type="button"
        className={styles.installBtn}
        onClick={handleInstall}
        aria-label={t('auto.installPrompt.install', 'Установить')}
      >
        {t('auto.installPrompt.install', 'Установить')}
      </button>
      <button
        type="button"
        className={styles.closeBtn}
        onClick={handleDismiss}
        aria-label={t('auto.installPrompt.dismiss', 'Закрыть')}
      >
        <Icon name="x" size={16} color="currentColor" />
      </button>
    </div>
  )
}

export default InstallPrompt
